'use client'

import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { CheckoutSalesLocationTable } from '@ancon/wildcat-types'
import HeadingText from '@ancon/wildcat-ui/web/orderweb/HeadingText'
import CardNumberIcon from '@ancon/wildcat-ui/shared/icons/card-number-outline.svg'

import Modal from '../../app/components/modal/Modal'
import {
  outletIsVisibleTableOrderConfigureModalSelector,
  outletSelectedOutletIdSelector,
  outletTableNumberValidateErrorSelector,
  outletTableNumberValidatePendingSelector,
} from '../store/outletSelector'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  outletClearTableNumberValidateError,
  outletSetIsVisibleTableOrderConfigureModal,
} from '../store/outletSlice'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import BodyText from '../../app/components/BodyText'
import Button from '../../app/components/Button'
import Input from '../../app/components/input/Input'
import { validateTableNumber } from '../store/outletThunks'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  checkoutCurrentCheckoutOutletIdSelector,
  checkoutFiltersTableNumberSelector,
} from '../../checkout/store/checkoutSelectors'
import FormInputErrorLabel from '../../app/components/FormInputErrorLabel'
import { AppTestIds } from '../../app/constants'

import styles from './TableOrderConfigureModal.module.scss'

const MaxTableNumber = 100000

type TableOrderConfigureModalProps = {
  isCheckoutPage?: boolean
  onSuccess: (salesLocation: CheckoutSalesLocationTable) => void
}

export default function TableOrderConfigureModal({
  isCheckoutPage,
  onSuccess,
}: TableOrderConfigureModalProps) {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const isOpen = useAppSelector(outletIsVisibleTableOrderConfigureModalSelector)
  const isValidatingTableNumber = useAppSelector(
    outletTableNumberValidatePendingSelector,
  )
  const tableValidateError = useAppSelector(
    outletTableNumberValidateErrorSelector,
  )
  const [tableNumber, setTableNumber] = useState<string | undefined>()

  useEffect(() => {
    if (isOpen && isCheckoutPage) {
      const checkoutTableNumber = checkoutFiltersTableNumberSelector(
        store.getState(),
      )
      setTableNumber(checkoutTableNumber ? `${checkoutTableNumber}` : '')
    }
  }, [isCheckoutPage, isOpen, store])

  function handleOnClose() {
    dispatch(outletSetIsVisibleTableOrderConfigureModal(false))
    setTableNumber('')
    if (tableValidateError) {
      dispatch(outletClearTableNumberValidateError())
    }
  }

  function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target
    const sanitizedValue = String(value).replace(/[^0-9]+/g, '')

    if (tableValidateError) {
      dispatch(outletClearTableNumberValidateError())
    }

    if (Number(sanitizedValue) <= MaxTableNumber) {
      setTableNumber(sanitizedValue)
    }
  }

  async function handleOnContinue() {
    const getOutletIdFun = isCheckoutPage
      ? checkoutCurrentCheckoutOutletIdSelector
      : outletSelectedOutletIdSelector

    const outletId = getOutletIdFun(store.getState())

    if (tableNumber && outletId) {
      try {
        const { id, number } = await dispatch(
          validateTableNumber({ tableNumber: Number(tableNumber), outletId }),
        ).unwrap()
        onSuccess({ id, number })
        handleOnClose()
      } catch (err) {
        // Handled error
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      mobileModalMode="bottom-sheet"
      onClose={handleOnClose}
      className={styles.modal}
      hideClose
    >
      <div className={styles.modalBody}>
        <CardNumberIcon />

        <HeadingText as="h2">{t('tableOrderConfigureModal.title')}</HeadingText>
        <div className={styles.fieldContainer}>
          <div className={styles.leftContainer}>
            <BodyText color="body-1">
              {t('tableOrderConfigureModal.tableNumber')}
            </BodyText>
            <BodyText color="body-2">
              {t('tableOrderConfigureModal.enterTableNumber')}
            </BodyText>
            <FormInputErrorLabel
              className={styles.errorLabel}
              error={tableValidateError?.message}
            />
          </div>
          <Input
            onChange={handleOnChange}
            invalid={!!tableValidateError}
            type="number"
            value={tableNumber}
            min="1"
            data-testid={AppTestIds.TableOrderConfigureModal.TableNumberInput}
          />
        </div>
      </div>
      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          disabled={isValidatingTableNumber}
          onClick={handleOnClose}
        >
          {t('tableOrderConfigureModal.cancel')}
        </Button>
        <Button
          loading={isValidatingTableNumber}
          disabled={
            !tableNumber || isValidatingTableNumber || !!tableValidateError
          }
          onClick={handleOnContinue}
          data-testid={AppTestIds.TableOrderConfigureModal.ContinueButton}
        >
          {t('tableOrderConfigureModal.continue')}
        </Button>
      </div>
    </Modal>
  )
}
